import { ComponentProps, useEffect, useRef } from 'react';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '@v2/shared/lib/utils';
import { useCart } from '@v2/shared/lib/cart';
import { useSale } from '@v2/shared/lib/megaSale';
import useToggle from '@v2/shared/lib/useToggle';
import Container from '@v2/shared/components/Container';
import SearchForm from '@v2/shared/components/SearchForm';
import Logo from '@v2/shared/Layouts/Full/Navigation/Logo';

import CartButton from './CartButton';
import SaleCategoriesDialog from './SaleCategoriesDialog';
import { SaleSearchProps } from './types';

import '@v2/shared/lib/fontAwesome';

/*
 * The header runs to the edges below md — the same measure TwoStripNavigation states,
 * and for the same reason. Tailwind derives `container`'s max-widths from `screens`, and
 * xs (375px) and sm (414px) are phone widths, so between 414px and 768px the container
 * stops growing and the header sits in a 414px column with the page showing down either
 * side.
 */
const HEADER_WIDTH = 'max-w-none md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl';

/**
 * What the search box says, on both widths.
 *
 * The legacy page said "Search this Promotion" for a reason: this box does not search the
 * site, and an unqualified "Search..." invites a title that is not on the page and can
 * only ever come back empty.
 */
const SEARCH_PLACEHOLDER = 'Search this promotion...';

type Props = {
  searchProps: SaleSearchProps;
  /** The brand's mark — the same shape its own `Layouts/Full` hands `Logo`. */
  logo?: ComponentProps<typeof Logo>;
};

/**
 * The sale page's header, for the brands whose site header is the two-strip one.
 *
 * MagSpot, MagZoom and TopMags: `TwoStripNavigation` with the sale's differences applied.
 * The first three are the ones DiscountMags' sale header applies to Full's; the fourth is
 * this header's own.
 *
 *   1. No site category bar. A sale has its own sections and the site's categories lead
 *      away from it; the hamburger opens *this sale's* sections instead — and only below
 *      md, because a desktop already has them as the pill bar `CategoryNav` draws under
 *      this header.
 *   2. The search box searches this sale rather than the site. `SearchForm` rather than
 *      the site `Search` beside it, because the suggestions are built by the layout from
 *      the sale document — see `useSaleSearch`.
 *   3. The cart icon opens the drawer instead of navigating to /cart.
 *   4. No account links, at any width. A sale page is somewhere a visitor lands to buy,
 *      and Log In / Sign Up beside the bag is the one pair on this chrome that leads away
 *      from it mid-purchase. Asked for in review — so if you are about to put them back,
 *      that is the decision you would be reversing.
 *
 *      Note what it costs, because nothing else on the page makes it up: there is then no
 *      route to the account area from a sale on these brands at all. The footer SaleShell
 *      draws is About/FAQ/Contact/Terms/Privacy/Accessibility and carries none, and this
 *      header is the only other chrome — signing in means leaving the sale, or checking
 *      out. DiscountMags is the exception; its sale header keeps its account button.
 *
 * One header for the three rather than one each, which is what makes this worth sharing:
 * TwoStripNavigation carries the note that TopMags "deliberately does not use this",
 * because its *site* header arranges the mark, the account chips and the bag differently.
 * Losing the category dropdown is what closes that gap, and losing the account links
 * closes the rest of it — what is left is a mark, a scoped search box and a bag, which all
 * three draw the same way.
 *
 * In normal flow rather than fixed, as all three site headers are, so nothing here
 * measures a height and there is no spacer. DiscountMags' sale header is fixed and keeps
 * its own file for that reason among others.
 *
 * Nothing here names a brand. `bg-primary` resolves to `--v2-primary`, which styles/v2.css
 * sets per brand under `[data-brand=...]`, so the coloured strip and the bag's badge are
 * TopMags red, MagSpot blue or MagZoom purple without a prop. The mark is the one thing
 * that cannot be: it is a file in the brand's own folder, so the brand passes it in.
 */
export default function Navigation({ searchProps, logo }: Props) {
  const { cartCount: loadedCartCount, loaded } = useCart();
  // The count is not known until the cart request answers — see Full's Navigation.
  const cartCount = loaded ? loadedCartCount : null;
  const { data, select, cartDrawer } = useSale();
  const categoriesMenuState = useToggle();
  const searchRef = useRef<HTMLInputElement>(null);
  const categories = data?.categories ?? [];

  /*
   * This header reserves nothing, and has to say so.
   *
   * The page's sticky section headings rest at
   * `top: calc(var(--sale-header-height, 82px) + var(--sale-nav-height, 0px))`, and
   * `scrollToSaleTarget` lands a deep link by the same sum. 82px is DiscountMags' fixed
   * header, which is the only one there was when the variable was written — and the
   * fallback is what a header that never publishes a value gets. This one is in normal
   * flow and scrolls away, so it holds nothing back and the right value is zero;
   * inheriting the fallback parked every section heading 82px down an empty viewport and
   * dropped every deep link the same distance short.
   *
   * Published here rather than in the page because it is a fact about the chrome, and the
   * chrome is the layout's. The page keeps publishing the measured height where there is
   * one to measure — nothing there changes.
   */
  useEffect(() => {
    document.documentElement.style.setProperty('--sale-header-height', '0px');

    // A block body, not a concise one: removeProperty returns the old value, and an
    // effect's destructor has to return void.
    return () => {
      document.documentElement.style.removeProperty('--sale-header-height');
    };
  }, []);

  return (
    <nav aria-label="Primary">
      <Container className={cn(HEADER_WIDTH, 'bg-white px-2.5 py-2 pt-4 md:px-5 md:py-3')}>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={categoriesMenuState.open}
            className="text-slate-500 md:hidden px-1 bg-transparent border-0 cursor-pointer"
            aria-label="Sale categories"
            aria-haspopup="dialog"
          >
            <FontAwesomeIcon icon={faBars} size="lg" />
          </button>
          {logo && <Logo {...logo} />}
          <CartButton cartCount={cartCount} onClick={cartDrawer.open} />
        </div>
      </Container>

      {/* The brand colour starts at md, as all three site headers do, and below md a rule
          replaces it. On a phone the strip is one white input edge to edge, and a band of
          brand behind it reads as a stray block of colour rather than a bar of
          navigation — it also cost the search box its contrast against the page. */}
      <div className="border-b border-slate-200 md:border-0 md:bg-primary">
        <Container className={cn(HEADER_WIDTH, 'px-2.5 md:px-5')}>
          {/* The search box and nothing else, at every width.

              This strip had a "Sections" button on the left from md, where
              TwoStripNavigation puts its category dropdown. It was wrong twice over: a
              desktop already has the sale's sections, as the pill bar `CategoryNav` draws
              directly under this header, and the button opened them in the phone's
              full-screen panel. DiscountMags' sale header has no desktop sections control
              either, for the same reason. Below md the hamburger in the strip above is
              the only trigger, which is where a sale's sections belong.

              So the box fills the strip rather than taking TwoStripNavigation's
              `md:w-2/3`, which was only ever the width left over beside the dropdown. */}
          <div className="py-2">
            {/* `flex` and `relative` are both load-bearing, and SearchForm cannot supply
                either: it renders a fragment — a label, the input, the submit button and
                the suggestions panel — so the row that joins the input to its button is
                this element, and so is the panel's positioned ancestor (it is
                `absolute top-12 inset-x-0`). Without the flex the button drops below the
                input; without the relative the suggestions open against the page.
                DiscountMags' sale header wraps it identically. */}
            <div className="relative flex w-full group">
              <SearchForm
                id="sale-search"
                ref={searchRef}
                searchAutoCompleteProps={searchProps}
                placeholder={SEARCH_PLACEHOLDER}
              />
            </div>
          </div>
        </Container>
      </div>

      <SaleCategoriesDialog
        isOpen={categoriesMenuState.isOpen}
        toggle={categoriesMenuState.toggle}
        close={categoriesMenuState.close}
        categories={categories}
        onSelect={(categoryId) => select(categoryId)}
      />
    </nav>
  );
}
